import { useState } from "react";

import { faEnvelope, faUser, faUserPlus } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";

import { Modal } from "@/components/Modals/Modal";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { Icon } from "@/components";

import { SendStepOptions } from "../components/SendStepOptions";
import { CampaignNodeTypes } from "../config/node-types";
import { AddStepOptions, StepInsertionContext } from "../types/step-insertion.types";

export const useNewNodeConditionModal = () => {
	const [showConditionalChoice, setShowConditionalChoice] = useState<boolean>(false);
	const [conditionalChoiceType, setConditionalChoiceType] = useState<
		| "conditional"
		| "linkedinInviteNode"
		| "leadHasEmail"
		| "leadHasLinkedin"
		| "emailOpened"
		| "emailClicked"
		| "leadPriority"
		| "linkedinConnected"
		| null
	>(null);

	const [showNewNodeModal, setShowNewNodeModal] = useState<boolean>(false);

	const renderConditionalChoiceModal = (
		handleAddConditionalNode: (
			edgeType?: "yesEdge" | "noEdge" | "normalEdge",
			type?:
				| "conditional"
				| "linkedinInviteNode"
				| "leadHasEmail"
				| "leadHasLinkedin"
				| "emailOpened"
				| "emailClicked"
				| "leadPriority"
				| "linkedinConnected"
		) => void,
		type?:
			| "conditional"
			| "linkedinInviteNode"
			| "leadHasEmail"
			| "leadHasLinkedin"
			| "emailOpened"
			| "emailClicked"
			| "leadPriority"
			| "linkedinConnected"
	) => {
		return showConditionalChoice === true ? (
			<Modal
				handleClose={() => setShowConditionalChoice(false)}
				handleSave={() => handleAddConditionalNode("yesEdge", type)}
				handleSecondSave={() => handleAddConditionalNode("noEdge", type)}
				isOpened={showConditionalChoice}
				secondSubmitButtonColor={ButtonColor.DANGER}
				secondSubmitButtonText="No Path"
				submitButtonColor={ButtonColor.SUCCESS}
				submitButtonText="Yes Path"
				title={"Choose a conditional"}
			>
				<div className="text-smd">
					You are adding a conditional node between two already existing nodes. Please choose if the
					following node should be executed if the condition is met or not.
				</div>
			</Modal>
		) : (
			""
		);
	};

	const renderModal = (
		handleAddEmailNode: (type: CampaignNodeTypes, options?: AddStepOptions) => void,
		handleAddConditionalNode: (
			edgeType?: "yesEdge" | "noEdge" | "normalEdge",
			type?:
				| "conditional"
				| "linkedinInviteNode"
				| "leadHasEmail"
				| "leadHasLinkedin"
				| "emailOpened"
				| "emailClicked"
				| "leadPriority"
				| "linkedinConnected"
		) => void,
		hasNextNode: boolean,
		nodeType: CampaignNodeTypes,
		edgeType?: "yesEdge" | "noEdge" | "normalEdge",
		hasNextYesNode?: boolean,
		hasNextNoNode?: boolean,
		/** The Campaign Builder's view of where the step goes (PREC-3897); absent elsewhere. */
		insertion?: StepInsertionContext
	) => {
		let hasNextNodeMerge = false;

		if (edgeType === "yesEdge" && hasNextYesNode) {
			hasNextNodeMerge = true;
		}

		if (edgeType === "noEdge" && hasNextNoNode) {
			hasNextNodeMerge = true;
		}

		if (hasNextNode && edgeType === "normalEdge") {
			hasNextNodeMerge = true;
		}

		return showNewNodeModal === true && !showConditionalChoice ? (
			<div className="absolute top-0 left-0 w-full h-full bg-black/50 z-[50000] flex items-center justify-center">
				<div className="w-[700px] min-w-[700px] h-[1/2] min-h-fit bg-white rounded-xl border border-gray-200 p-4 flex flex-col justify-between">
					<div className="min-w-full grid grid-cols-2  gap-x-8 gap-y-4">
						<div className="text-smd col-span-full">Add step</div>

						<SendStepOptions edgeType={edgeType} insertion={insertion} onAdd={handleAddEmailNode} />
						<div className="min-w-[calc(50%-2rem)]">
							<div className="text-sm text-text-meta mb-4">Conditions</div>
							<div className="flex flex-col gap-2 mb-6 ">
								{nodeType === "linkedinInviteNode" ? (
									<Button
										className="w-full"
										color={ButtonColor.ACTION_SECONDARY}
										image={<Icon className="w-4 h-4 mr-2" icon={faUserPlus} />}
										size={ButtonSize.ML}
										textClassName="!text-left items-start !justify-start"
										title="Lead accepted invitation"
										onClick={() => {
											if (hasNextNodeMerge) {
												setShowConditionalChoice(true);
												setConditionalChoiceType("linkedinInviteNode");
											} else {
												handleAddConditionalNode("normalEdge", "linkedinInviteNode");
											}
										}}
									/>
								) : (
									<Button
										className="w-full"
										color={ButtonColor.ACTION_SECONDARY}
										image={<Icon className="w-4 h-4 mr-2" icon={faUserPlus} />}
										size={ButtonSize.ML}
										textClassName="!text-left items-start !justify-start"
										title="Lead is connected"
										onClick={
											hasNextNodeMerge
												? () => {
														setShowConditionalChoice(true);
														setConditionalChoiceType("linkedinConnected");
													}
												: () => {
														handleAddConditionalNode("normalEdge", "linkedinConnected");
													}
										}
									/>
								)}
								<Button
									className="w-full"
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="w-4 h-4 mr-2" icon={faUser} />}
									size={ButtonSize.ML}
									textClassName="!text-left items-start !justify-start"
									title="Lead has email"
									onClick={() => {
										if (hasNextNodeMerge) {
											setShowConditionalChoice(true);
											setConditionalChoiceType("leadHasEmail");
										} else {
											handleAddConditionalNode("normalEdge", "leadHasEmail");
										}
									}}
								/>
								<Button
									className="w-full"
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="w-4 h-4 mr-2" icon={faUser} />}
									size={ButtonSize.ML}
									textClassName="!text-left items-start !justify-start"
									title="Lead has linkedin"
									onClick={() => {
										if (hasNextNodeMerge) {
											setShowConditionalChoice(true);
											setConditionalChoiceType("leadHasLinkedin");
										} else {
											handleAddConditionalNode("normalEdge", "leadHasLinkedin");
										}
									}}
								/>
								<Button
									className="w-full"
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="w-4 h-4 mr-2" icon={faUser} />}
									size={ButtonSize.ML}
									textClassName="!text-left items-start !justify-start"
									title="Lead Priority"
									onClick={() => {
										if (hasNextNodeMerge) {
											setShowConditionalChoice(true);
											setConditionalChoiceType("leadPriority");
										} else {
											handleAddConditionalNode("normalEdge", "leadPriority");
										}
									}}
								/>
								{nodeType === "emailNode" && (
									<Button
										className="w-full"
										color={ButtonColor.ACTION_SECONDARY}
										image={<Icon className="w-4 h-4 mr-2" icon={faEnvelope} />}
										size={ButtonSize.ML}
										textClassName="!text-left items-start !justify-start"
										title="E-Mail has been opened"
										onClick={() => {
											if (hasNextNodeMerge) {
												setShowConditionalChoice(true);
												setConditionalChoiceType("emailOpened");
											} else {
												handleAddConditionalNode("normalEdge", "emailOpened");
											}
										}}
									/>
								)}
								{nodeType === "emailNode" && (
									<Button
										className="w-full"
										color={ButtonColor.ACTION_SECONDARY}
										image={<Icon className="w-4 h-4 mr-2" icon={faEnvelope} />}
										size={ButtonSize.ML}
										textClassName="!text-left items-start !justify-start"
										title="E-Mail has been clicked"
										onClick={() => {
											if (hasNextNodeMerge) {
												setShowConditionalChoice(true);
												setConditionalChoiceType("emailClicked");
											} else {
												handleAddConditionalNode("normalEdge", "emailClicked");
											}
										}}
									/>
								)}
							</div>
						</div>
						<div className="self-end ">
							<p className="mb-2 font-normal text-xs text-gray-700">
								If a step fails, the next step will be executed.
							</p>
							<p className="font-normal text-xs text-gray-700">
								Sequences stop automatically as soon as the lead responds to a mesage.
							</p>
						</div>
					</div>
					<div className="flex justify-end w-auto mt-8">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title="Cancel"
							onClick={() => setShowNewNodeModal(false)}
						/>
					</div>
				</div>
			</div>
		) : (
			""
		);
	};

	return {
		showConditionalChoice,
		setShowConditionalChoice,
		renderModal,
		renderConditionalChoiceModal,
		showNewNodeModal,
		setShowNewNodeModal,
		conditionalChoiceType,
	};
};
